import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Chip,
  Breadcrumbs,
  Link as MuiLink,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  RadioGroup,
  Radio,
  FormControlLabel,
  Autocomplete,
} from '@mui/material';
import {
  Science as RunsIcon,
  DeleteOutline as DeleteIcon,
  AccountTree as StudyIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { analysisFindingsAPI, studyAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { canDeleteAnalysisRun, canEditLabData } from '../../utils/roleUtils';
import { getSampleColumns, renderSampleRow } from './sampleFindings';

/**
 * Analysis run detail page.
 *
 * Shows run metadata + stats + a sample of the first 100 findings (kind-
 * aware). The sample is for verification / spot-checking ("did my run
 * work correctly?"), not for browsing all findings — full per-specimen
 * findings live on the specimen analysis tab when it ships.
 */

function formatUploader(run) {
  if (!run) return '—';
  if (run.uploaded_by_first_name || run.uploaded_by_last_name) {
    return `${run.uploaded_by_first_name || ''} ${run.uploaded_by_last_name || ''}`.trim();
  }
  return run.uploaded_by_username || '—';
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

const AnalysisRunDetail = () => {
  const { run_number: runNumberStr } = useParams();
  const runNumber = parseInt(runNumberStr, 10);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    analysisFindingsAPI
      .getRun(runNumber)
      .then((res) => {
        if (cancelled) return;
        setData(res.data);
        setError(null);
        setNotFound(false);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err.response?.status === 404) {
          setNotFound(true);
        } else {
          setError(err.response?.data?.msg || err.message || 'Failed to load run');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [runNumber, reloadKey]);

  return (
    <Box sx={{ p: 3, maxWidth: 1300 }}>
      <Breadcrumbs sx={{ mb: 2 }}>
        <MuiLink component={RouterLink} to="/analysis/runs" underline="hover">
          Analysis Runs
        </MuiLink>
        <Typography color="text.primary">Run #{runNumber}</Typography>
      </Breadcrumbs>

      {loading && (
        <Stack direction="row" spacing={1} alignItems="center">
          <CircularProgress size={20} />
          <Typography variant="body2">Loading…</Typography>
        </Stack>
      )}

      {!loading && notFound && (
        <Alert severity="warning">
          Run #{runNumber} doesn't exist. It may have been deleted, or the number is wrong.{' '}
          <MuiLink component={RouterLink} to="/analysis/runs">Back to all runs</MuiLink>
        </Alert>
      )}

      {!loading && error && (
        <Alert severity="error">Failed to load run: {error}</Alert>
      )}

      {!loading && !notFound && !error && data && (
        <RunBody data={data} onChanged={() => setReloadKey((k) => k + 1)} />
      )}
    </Box>
  );
};

function RunBody({ data, onChanged }) {
  const { run, kind_counts, sample_findings, projects = [] } = data;
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const canDelete = canDeleteAnalysisRun(currentUser);
  // Grouping into a study mutates project records, so gate on the same roles
  // the backend allows for POST /api/studies.
  const canGroupIntoStudy = canEditLabData(currentUser) && projects.length > 1;
  const isDeleted = run.status === 'deleted';
  const isSuperseded = run.status === 'superseded';

  const [dialogOpen, setDialogOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [restoring, setRestoring] = useState(false);

  const [studyDialogOpen, setStudyDialogOpen] = useState(false);
  const [studyMode, setStudyMode] = useState('new'); // 'new' | 'existing'
  const [studyName, setStudyName] = useState('');
  const [existingStudies, setExistingStudies] = useState([]);
  const [selectedStudy, setSelectedStudy] = useState(null);
  const [savingStudy, setSavingStudy] = useState(false);
  const projectIds = projects.map((p) => p.project_id);

  // Projects that would change study membership, given the chosen target.
  // For "new": any project already in a study moves. For "existing": any
  // project not already in the selected study moves (from none or elsewhere).
  const projectsMoving =
    studyMode === 'existing'
      ? projects.filter((p) => p.current_study_id !== (selectedStudy?.id || null))
      : projects.filter((p) => p.current_study_id);

  // Load the study list the first time the dialog is opened.
  useEffect(() => {
    if (!studyDialogOpen || existingStudies.length > 0) return;
    studyAPI
      .getAll('?limit=500')
      .then((res) => setExistingStudies(res.data.studies || []))
      .catch(() => { /* dropdown just stays empty; create-new still works */ });
  }, [studyDialogOpen, existingStudies.length]);

  const goToStudy = (study, verb, count) => {
    toast.success(
      ({ closeToast }) => (
        <span>
          {verb} Study #{study.study_number}
          {count != null ? ` (${count} project${count === 1 ? '' : 's'})` : ''}.{' '}
          <MuiLink
            component="button"
            type="button"
            onClick={() => { closeToast(); navigate(`/studies/${study.id}`); }}
          >
            View study
          </MuiLink>
        </span>
      ),
      { autoClose: 8000 }
    );
  };

  const closeStudyDialog = () => {
    if (savingStudy) return;
    setStudyDialogOpen(false);
    setStudyName('');
    setSelectedStudy(null);
    setStudyMode('new');
  };

  const studySubmitDisabled =
    savingStudy ||
    (studyMode === 'new' ? !studyName.trim() : !selectedStudy);

  const handleStudySubmit = async () => {
    if (studySubmitDisabled) return;
    setSavingStudy(true);
    try {
      if (studyMode === 'new') {
        const res = await studyAPI.create({ name: studyName.trim(), project_ids: projectIds });
        goToStudy(res.data, 'Created', projects.length);
      } else {
        const res = await studyAPI.addProjects(selectedStudy.id, projectIds);
        goToStudy(selectedStudy, 'Added projects to', res.data.added);
      }
      setStudyDialogOpen(false);
      setStudyName('');
      setSelectedStudy(null);
      setStudyMode('new');
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.msg || err.message || 'Failed to update study');
    } finally {
      setSavingStudy(false);
    }
  };

  const handleRestore = async () => {
    setRestoring(true);
    try {
      await analysisFindingsAPI.restoreRun(run.run_number);
      toast.success(`Run #${run.run_number} restored.`);
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.msg || err.message || 'Failed to restore run');
    } finally {
      setRestoring(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const res = await analysisFindingsAPI.deleteRun(run.run_number, reason.trim() || undefined);
      const w = (res.data.warnings || [])[0];
      if (w) toast.warning(w);
      toast.success(
        ({ closeToast }) => (
          <span>
            Run #{run.run_number} deleted — {Number(res.data.findings).toLocaleString()} findings hidden.{' '}
            <MuiLink
              component="button"
              type="button"
              onClick={() => { closeToast(); handleRestore(); }}
            >
              Undo
            </MuiLink>
          </span>
        ),
        { autoClose: 8000 }
      );
      setDialogOpen(false);
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.msg || err.message || 'Failed to delete run');
    } finally {
      setDeleting(false);
    }
  };

  // All findings in a single run share one kind (one parser → one result type).
  // Take the kind from the first sample finding (or fall back to kind_counts).
  const kind =
    (sample_findings && sample_findings[0] && sample_findings[0].kind) ||
    (kind_counts && kind_counts[0] && kind_counts[0].kind) ||
    null;
  const sampleColumns = getSampleColumns(kind);

  return (
    <>
      <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
        <RunsIcon color="primary" sx={{ mr: 1 }} />
        <Typography variant="h5">
          Run #{run.run_number}
          <Typography component="span" variant="h6" color="text.secondary" sx={{ ml: 1.5 }}>
            — {run.producer_name}
          </Typography>
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        {canDelete && !isDeleted && (
          <Button
            variant="outlined"
            color="error"
            size="small"
            startIcon={<DeleteIcon />}
            onClick={() => setDialogOpen(true)}
          >
            Delete run
          </Button>
        )}
      </Stack>

      {isDeleted && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            canDelete ? (
              <Button color="inherit" size="small" onClick={handleRestore} disabled={restoring}>
                {restoring ? 'Restoring…' : 'Restore'}
              </Button>
            ) : null
          }
        >
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            This run is deleted{run.deleted_by_username ? ` (by ${run.deleted_by_username}` : ''}
            {run.deleted_at ? `${run.deleted_by_username ? ', ' : ' ('}${formatDate(run.deleted_at)}` : ''}
            {run.deleted_by_username || run.deleted_at ? ')' : ''} — hidden from all analysis views.
          </Typography>
          <Typography variant="caption">
            Its findings are retained and reappear if you restore it.
            {run.deleted_reason ? ` Reason: ${run.deleted_reason}` : ' No reason was given.'}
          </Typography>
        </Alert>
      )}

      {isSuperseded && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Superseded
          {run.superseded_by_run_number != null ? (
            <>
              {' '}by{' '}
              <MuiLink component={RouterLink} to={`/analysis/runs/${run.superseded_by_run_number}`}>
                run #{run.superseded_by_run_number}
              </MuiLink>
            </>
          ) : null}
          {' '}— hidden from findings views; kept in run history.
        </Alert>
      )}

      {/* Metadata */}
      <Paper sx={{ p: 3, mb: 3 }} elevation={1}>
        <Stack spacing={1}>
          <MetadataRow label="Pipeline">
            {run.producer_name}
            {run.producer_version && (
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                @ {run.producer_version}
              </Typography>
            )}
          </MetadataRow>
          <MetadataRow label="Module">{run.module_name || '—'}</MetadataRow>
          <MetadataRow label="Uploaded by">{formatUploader(run)}</MetadataRow>
          <MetadataRow label="Date">{formatDate(run.imported_at)}</MetadataRow>
          {run.notes && <MetadataRow label="Notes">{run.notes}</MetadataRow>}
        </Stack>
      </Paper>

      {/* Stats */}
      <Paper sx={{ p: 3, mb: 3 }} elevation={1}>
        <Typography variant="subtitle1" sx={{ mb: 1.5 }}>Summary</Typography>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <Chip
            label={`${Number(run.total_findings).toLocaleString()} findings`}
            color="primary"
            variant="outlined"
          />
          <Chip
            label={`${Number(run.distinct_specimens).toLocaleString()} specimens`}
            variant="outlined"
          />
          {kind_counts.map((k) => (
            <Chip
              key={k.kind}
              label={`${Number(k.count).toLocaleString()} ${k.kind}`}
              size="small"
            />
          ))}
        </Stack>
      </Paper>

      {/* Projects */}
      <Paper sx={{ p: 3, mb: 3 }} elevation={1}>
        <Stack direction="row" alignItems="center" sx={{ mb: 1.5 }}>
          <Typography variant="subtitle1">
            Projects {projects.length > 0 && `(${projects.length})`}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          {canGroupIntoStudy && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<StudyIcon />}
              onClick={() => setStudyDialogOpen(true)}
            >
              Group into a study
            </Button>
          )}
        </Stack>
        {projects.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            None of this run's specimens are linked to a project.
          </Typography>
        ) : (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {projects.map((p) => (
              <Chip
                key={p.project_id}
                component={RouterLink}
                to={`/projects/${p.project_id}`}
                clickable
                variant="outlined"
                label={
                  `Project #${p.project_number}` +
                  (p.disease && p.disease.trim().toLowerCase() !== 'unknown' ? ` — ${p.disease}` : '') +
                  ` (${Number(p.specimen_count).toLocaleString()} specimen${Number(p.specimen_count) === 1 ? '' : 's'})`
                }
              />
            ))}
          </Stack>
        )}
      </Paper>

      {/* Sample findings */}
      <Paper sx={{ p: 0 }} elevation={1}>
        <Box sx={{ p: 3, pb: 1 }}>
          <Typography variant="subtitle1">Sample findings</Typography>
          <Typography variant="caption" color="text.secondary">
            {sample_findings.length === 0
              ? 'No findings in this run.'
              : `Showing the first ${sample_findings.length} of ${Number(run.total_findings).toLocaleString()}.`}
            {' '}For per-specimen views of all findings, use the specimen analysis tab on each specimen.
          </Typography>
        </Box>
        {sample_findings.length > 0 && (
          <>
            <Divider />
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>WUID</TableCell>
                    {sampleColumns.map((c) => (
                      <TableCell key={c}>{c}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sample_findings.map((f) => (
                    <TableRow key={f.finding_id} hover>
                      <TableCell>{f.wuid ?? '—'}</TableCell>
                      {renderSampleRow(f, f.kind).map((v, j) => (
                        <TableCell key={j}>{v}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Paper>

      <Dialog open={dialogOpen} onClose={() => !deleting && setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Delete run #{run.run_number}?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            This hides{' '}
            <strong>{Number(run.total_findings).toLocaleString()} findings</strong> across{' '}
            <strong>{Number(run.distinct_specimens).toLocaleString()} specimens</strong> from all analysis
            views. Nothing is permanently removed — you can restore the run from this page (or hit Undo on
            the confirmation).
          </DialogContentText>
          <TextField
            label="Reason (optional)"
            placeholder="e.g. wrong clustering parameters"
            fullWidth
            multiline
            minRows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={deleting}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={deleting}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete run'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={studyDialogOpen} onClose={closeStudyDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Group {projects.length} projects into a study</DialogTitle>
        <DialogContent>
          <RadioGroup
            value={studyMode}
            onChange={(e) => setStudyMode(e.target.value)}
            sx={{ mb: 1 }}
          >
            <FormControlLabel
              value="new"
              control={<Radio size="small" />}
              disabled={savingStudy}
              label="Create a new study"
            />
            {studyMode === 'new' && (
              <TextField
                label="Study name"
                placeholder="e.g. FRESH cohort 2026"
                fullWidth
                autoFocus
                required
                value={studyName}
                onChange={(e) => setStudyName(e.target.value)}
                disabled={savingStudy}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !studySubmitDisabled) handleStudySubmit();
                }}
                sx={{ ml: 4, mb: 1, width: 'calc(100% - 32px)' }}
              />
            )}
            <FormControlLabel
              value="existing"
              control={<Radio size="small" />}
              disabled={savingStudy}
              label="Add to an existing study"
            />
            {studyMode === 'existing' && (
              <Autocomplete
                options={existingStudies}
                value={selectedStudy}
                onChange={(_e, v) => setSelectedStudy(v)}
                disabled={savingStudy}
                getOptionLabel={(s) =>
                  `Study #${s.study_number} — ${s.name}` +
                  (s.project_count != null ? ` (${s.project_count} projects)` : '')
                }
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={(params) => (
                  <TextField {...params} label="Choose study" placeholder="Search studies…" />
                )}
                sx={{ ml: 4, mb: 1, width: 'calc(100% - 32px)' }}
              />
            )}
          </RadioGroup>

          <Divider sx={{ my: 1.5 }} />

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Projects to include:
          </Typography>
          <Stack spacing={0.5} sx={{ mb: projectsMoving.length > 0 ? 2 : 0 }}>
            {projects.map((p) => {
              const stays =
                studyMode === 'existing' && p.current_study_id === (selectedStudy?.id || null) && p.current_study_id;
              const moves =
                studyMode === 'existing'
                  ? p.current_study_id !== (selectedStudy?.id || null) && p.current_study_id
                  : p.current_study_id;
              return (
                <Typography key={p.project_id} variant="body2">
                  • Project #{p.project_number}
                  {stays && (
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      (already in this study)
                    </Typography>
                  )}
                  {moves && (
                    <Typography component="span" variant="caption" color="warning.main" sx={{ ml: 1 }}>
                      (currently in Study #{p.current_study_number} — will move)
                    </Typography>
                  )}
                </Typography>
              );
            })}
          </Stack>
          {projectsMoving.length > 0 && (
            <Alert severity="warning">
              {projectsMoving.length} project{projectsMoving.length === 1 ? ' is' : 's are'} already
              in another study. A project belongs to one study at a time, so{' '}
              {projectsMoving.length === 1 ? 'it' : 'they'} will be moved
              {studyMode === 'existing' ? ' into the selected study' : ' into the new study'}.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeStudyDialog} disabled={savingStudy}>Cancel</Button>
          <Button
            onClick={handleStudySubmit}
            variant="contained"
            disabled={studySubmitDisabled}
            startIcon={<StudyIcon />}
          >
            {savingStudy
              ? 'Saving…'
              : studyMode === 'new'
                ? 'Create study'
                : 'Add to study'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function MetadataRow({ label, children }) {
  return (
    <Stack direction="row" spacing={2}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ flexGrow: 1 }}>
        {children}
      </Typography>
    </Stack>
  );
}

export default AnalysisRunDetail;
